import { ContentInteractionEvent, DeepMaybe, HoverInteractionBlock } from './types'

// Event validation
interface ValidationResult {
  isValid: boolean
  errors: string[]
  event: ContentInteractionEvent
}

const validateContent = (event: DeepMaybe<ContentInteractionEvent>) => {
  const errors = []
  const content = event.content
  if (!content) {
    errors.push('Content block is required')
  } else {
    if (!content.id) {
      errors.push('Content ID is required')
    }
    if (!content.type) {
      errors.push('Content type is required')
    }
  }
  return errors
}

const validateInteraction = (event: DeepMaybe<ContentInteractionEvent>) => {
  const errors = []
  const interaction = event.interaction
  if (!interaction) {
    errors.push('Interaction block is required')
  } else {
    if (!interaction.action) {
      errors.push('Interaction action is required')
    }

    // Conditional validation for hover interactions
    if (interaction.action === 'hover') {
      const hoverInteraction = interaction as Partial<HoverInteractionBlock>
      if (!hoverInteraction.hover) {
        errors.push('Hover interaction must have hover details')
      } else {
        if (!hoverInteraction.hover.dwell_ms || hoverInteraction.hover.dwell_ms <= 0) {
          errors.push('Hover interaction requires valid dwell_ms > 0')
        }
      }
    }
  }
  return errors
}

const validatePlacement = (presentation: DeepMaybe<ContentInteractionEvent['presentation']>) => {
  const errors = []
  const placement = presentation.placement
  if (!placement) {
    errors.push('Placement block is required')
  } else {
    if (!placement.container) {
      errors.push('Placement container is required')
    }
    if (typeof placement.container_position !== 'number') {
      errors.push('Container position must be a number')
    }
    if (typeof placement.item_position !== 'number') {
      errors.push('Item position must be a number')
    }
    if (!placement.layout) {
      errors.push('Placement layout is required')
    }
  }
  return errors
}

const validateArtwork = (presentation: DeepMaybe<ContentInteractionEvent['presentation']>) => {
  const errors = []
  const artwork = presentation.artwork
  if (!artwork) {
    errors.push('Artwork block is required')
  } else {
    const hasImageUrl = !!artwork.image_url
    const hasVideoUrl = !!artwork.video_url

    if (!hasImageUrl && !hasVideoUrl) {
      errors.push('At least one media URL (image_url or video_url) should be provided')
    }
  }
  return errors
}

const validatePresentation = (event: DeepMaybe<ContentInteractionEvent>) => {
  const errors = []
  const presentation = event.presentation
  if (!presentation) {
    errors.push('Presentation block is required')
  } else {
    if (!presentation.surface) {
      errors.push('Presentation surface is required')
    }

    errors.push(...validatePlacement(presentation))
    errors.push(...validateArtwork(presentation))
  }
  return errors
}

export const validateContentInteractionEvent = (event: DeepMaybe<ContentInteractionEvent>): ValidationResult => {
  const errors = validateContent(event).concat(validateInteraction(event)).concat(validatePresentation(event))

  // Attribution is optional - backend provides it for authenticated users, null for unauthenticated
  // We allow null/undefined and let the backend handle it

  return {
    isValid: errors.length === 0,
    errors,
    event: event as ContentInteractionEvent,
  }
}
